/**
 * Route-guard helpers for TanStack Router `beforeLoad`.
 *
 * Three guards covering the auth surfaces enumerated in ARCH-002 §1:
 *   - requireSubscriberOrOperator: any signed-in user. Used by /portal/*.
 *   - requireOperator:             operator-only. Used by /eric/*. Returns 404
 *                                  for non-operators so the surface stays
 *                                  invisible (ARCH-002 §1 + T-018 AC).
 *   - redirectIfSignedIn:          /auth/sign-in skips the form if a session
 *                                  is already loaded.
 *
 * These are pure functions called from a route's `beforeLoad`. They throw
 * `redirect(...)` (TanStack Router's documented control-flow primitive) or
 * `notFound()` to halt navigation; consumers should not try/catch them.
 *
 * Code-split note (T-018 AC #3): operator routes import the operator guard
 * but the guard itself is tree-shakeable; subscriber bundles only pull
 * `requireSubscriberOrOperator`. The bigger code-split win lands when
 * /portal/* and /eric/* live in separate route file trees with lazy
 * imports (Phase F/G/I); the guards in this file are not what creates
 * the operator surface visibility, the route tree split is.
 */

import { redirect, notFound } from '@tanstack/react-router';
import {
  getCurrentSession,
  getRoleFromSession,
  isOperatorSessionExpired,
} from './auth.js';

/**
 * Guard for /portal/*. Redirects unauthenticated users to /auth/sign-in with
 * the original path stashed in the redirect search param so we can route back
 * after sign-in (handled by /auth/callback in a future iteration; the param
 * is harmless today).
 */
export async function requireSubscriberOrOperator(opts?: { redirectFrom?: string }) {
  const session = await getCurrentSession();
  if (!session) {
    throw redirect({
      to: '/auth/sign-in',
      ...(opts?.redirectFrom
        ? { search: { redirect: opts.redirectFrom } }
        : {}),
    });
  }
  return session;
}

/**
 * Guard for /eric/*. Three outcomes:
 *   1. No session                -> redirect to /auth/sign-in.
 *   2. Session but not operator  -> notFound() (do not reveal the surface).
 *   3. Operator session > 7 days -> sign out and redirect to /auth/sign-in
 *                                   per ARCH-002 §7 (shorter operator window).
 */
export async function requireOperator(opts?: { redirectFrom?: string }) {
  const session = await getCurrentSession();
  if (!session) {
    throw redirect({
      to: '/auth/sign-in',
      ...(opts?.redirectFrom
        ? { search: { redirect: opts.redirectFrom } }
        : {}),
    });
  }
  if (getRoleFromSession(session) !== 'operator') {
    // Do not throw redirect; the operator surface must not leak existence to
    // non-operators (T-018 AC #2).
    throw notFound();
  }
  if (isOperatorSessionExpired(session)) {
    throw redirect({
      to: '/auth/sign-in',
      search: { reason: 'operator-session-expired' },
    });
  }
  return session;
}

/**
 * Guard for /auth/sign-in: send already-signed-in users to their role-home.
 * Stops the form from re-prompting an authenticated subscriber.
 */
export async function redirectIfSignedIn() {
  const session = await getCurrentSession();
  if (!session) return null;
  const role = getRoleFromSession(session);
  throw redirect({
    to: role === 'operator' ? '/eric' : '/portal',
  });
}
